<template>
  <v-card style="height: 100%">
    <project-flow-navigation-toolbar
      :title="buildingName"
    ></project-flow-navigation-toolbar>

    <v-toolbar dark dense>
      <v-tabs v-model="tab">
        <v-tab href="#details"> Details </v-tab>
        <v-tab href="#media"> Media </v-tab>
        <v-tab href="#pois"> Points of interest </v-tab>
        <v-tab href="#apartmentTypes"> Apartment Types </v-tab>
        <v-tab href="#apartmentOrientations"> Apartment Orientations </v-tab>
        <v-tab href="#apartments"> Apartments </v-tab>
      </v-tabs>
    </v-toolbar>

    <v-tabs-items
      style="height: calc(100% - 112px); position: relative"
      v-model="tab"
    >
      <v-tab-item style="height: 100%" value="details">
        <v-card class="full-height-card">
          <v-card-text>

            <!-- Basic fields -->
            <v-row class="mt-2">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  outlined
                  dense
                  v-model="building.name"
                  label="Name"
                  disabled
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  outlined
                  dense
                  v-model="building.title"
                  label="Title"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Publish toggle + JSON preview -->
            <v-row align="center" class="mb-2">
              <v-col cols="12" sm="4" md="3" class="d-flex align-center">
                <v-switch
                  inset
                  hide-details
                  class="mt-0"
                  v-model="building.publish"
                  :disabled="checkboxDisabled"
                  :label="building.publish ? 'Published' : 'Unpublished'"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="7" md="5">
                <div class="caption text--secondary mb-1">Properties (JSON)</div>
                <div
                  class="json-preview"
                  style="height: 56px; overflow: hidden; cursor: pointer; border: 1px solid rgba(255,255,255,0.24); border-radius: 4px; padding: 6px 8px; position: relative;"
                  @click="openPropertiesDialog"
                >
                  <pre style="margin: 0; font-size: 11px; line-height: 1.4; white-space: pre-wrap; word-break: break-all;">{{ JSON.stringify(building.properties, null, 2) }}</pre>
                  <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 20px; background: linear-gradient(to bottom, transparent, #1e1e1e);"></div>
                </div>
              </v-col>
            </v-row>

            <v-dialog v-model="propertiesEditorDialog" max-width="700px">
              <v-card :style="propertiesDialogCardStyle">
                <v-card-title class="subtitle-1 flex-shrink-0 d-flex align-center">
                  Properties (JSON)
                  <v-fade-transition>
                    <span v-if="propertiesDialogDirty" class="caption amber--text d-flex align-center ml-2">
                      <v-icon x-small color="amber" class="mr-1">mdi-circle-medium</v-icon>Unsaved changes
                    </span>
                  </v-fade-transition>
                  <v-spacer></v-spacer>
                  <v-btn icon small @click="propertiesEditorDialog = false">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="flex-grow-1" style="overflow: hidden; padding-bottom: 0;">
                  <v-jsoneditor
                    v-model="building.properties"
                    :plus="false"
                    :height="propertiesEditorHeight"
                    @onChangeText="onJsonEditorChange"
                    :options="{ mode: 'text' }"
                  ></v-jsoneditor>
                </v-card-text>
                <v-card-actions class="flex-shrink-0">
                  <v-spacer></v-spacer>
                  <v-btn text :disabled="!propertiesDialogDirty" @click="discardPropertiesDialog">Discard</v-btn>
                  <v-btn color="blue darken-1" text @click="propertiesEditorDialog = false">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Extra properties fields from JSON (excludes paymentPlan +
                 compare-amenity keys, which have dedicated controls below) -->
            <v-row>
              <template v-for="(item, key) in building.properties">
                <v-col
                  v-if="!isHiddenPropertyKey(key)"
                  :key="key"
                  cols="6"
                  sm="4"
                  md="2"
                >
                  <v-text-field
                    outlined
                    dense
                    v-model="building.properties[key]"
                    :label="key"
                  ></v-text-field>
                </v-col>
              </template>
            </v-row>

            <!-- Compare amenities: inherited by this building's apartments in
                 the Property Navigator compare view. An apartment's own amenity
                 value always overrides the building's. Collapsed by default. -->
            <div class="mt-4 mb-2">
              <div
                class="d-flex align-center mb-2"
                style="cursor: pointer; user-select: none;"
                @click="amenitiesExpanded = !amenitiesExpanded"
              >
                <span class="subtitle-1">Compare amenities</span>
                <v-icon class="ml-1">
                  {{ amenitiesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                <v-spacer></v-spacer>
              </div>
              <v-expand-transition>
                <div v-show="amenitiesExpanded">
                  <div class="d-flex align-center mb-2">
                    <v-spacer></v-spacer>
                    <v-switch
                      inset
                      hide-details
                      class="mt-0"
                      v-model="applyBuildingAmenities"
                      :label="
                        applyBuildingAmenities
                          ? 'Applied to all apartments'
                          : 'Apply building amenities to all apartments'
                      "
                    ></v-switch>
                  </div>
                  <div class="caption text--secondary mb-2">
                    When enabled, these amenities are shown for every apartment in
                    this building unless the apartment sets its own value.
                  </div>
                  <v-row>
                    <v-col
                      v-for="key in compareAmenityKeys"
                      :key="key"
                      cols="6"
                      sm="4"
                      md="2"
                    >
                      <v-select
                        outlined
                        dense
                        hide-details
                        :label="key"
                        :items="amenityOptions"
                        :value="getCompareAmenity(key)"
                        @change="setCompareAmenity(key, $event)"
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-combobox
                        v-model="activitiesModel"
                        label="activities"
                        hint="Type an activity and press Enter (shown as a comma-separated list)"
                        persistent-hint
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        outlined
                        dense
                      ></v-combobox>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>

            <!-- Payment Plans -->
            <div class="mt-6 mb-2">
              <div class="d-flex align-center mb-3">
                <span class="subtitle-1">Payment Plans</span>
                <v-fade-transition>
                  <span v-if="isDraggingMilestone" class="caption ml-3 blue--text text--lighten-2 d-flex align-center">
                    <v-icon x-small color="blue lighten-2" class="mr-1">mdi-swap-vertical</v-icon>Drag to reorder
                  </span>
                </v-fade-transition>
                <v-spacer></v-spacer>
                <v-btn
                  small
                  outlined
                  color="blue darken-1"
                  :disabled="!paymentPlanItems.length"
                  @click="applyPlanDialogVisible = true"
                >
                  <v-icon small left>mdi-content-copy</v-icon>
                  Apply to other buildings
                </v-btn>
              </div>

              <!-- Column headers -->
              <div class="milestone-row milestone-header">
                <div class="ms-col-order caption text--secondary">#</div>
                <div class="ms-col-handle"></div>
                <div class="ms-col-pct caption text--secondary">%</div>
                <div class="ms-col-desc caption text--secondary">Description</div>
                <div class="ms-col-actions"></div>
              </div>
              <v-divider class="mb-1"></v-divider>

              <!-- Draggable existing rows -->
              <draggable
                v-if="paymentPlanItems.length"
                :list="building.properties.paymentPlan"
                handle=".drag-handle"
                ghost-class="milestone-drag-ghost"
                @start="isDraggingMilestone = true"
                @end="onPaymentPlanReorder"
              >
                <div
                  v-for="(item, index) in paymentPlanItems"
                  :key="index"
                  class="milestone-row"
                >
                  <div class="ms-col-order caption text--secondary">{{ index + 1 }}.</div>
                  <div class="ms-col-handle d-flex justify-center align-center">
                    <v-tooltip bottom open-delay="400">
                      <template v-slot:activator="{ on }">
                        <v-icon small class="drag-handle" v-on="on">mdi-reorder-horizontal</v-icon>
                      </template>
                      <span>Drag to reorder</span>
                    </v-tooltip>
                  </div>
                  <div class="ms-col-pct">
                    <v-text-field
                      dense
                      outlined
                      type="number"
                      v-model.number="item.percentage"
                      suffix="%"
                      min="0"
                      max="100"
                      hide-details
                    ></v-text-field>
                  </div>
                  <div class="ms-col-desc">
                    <v-text-field
                      dense
                      outlined
                      v-model="item.description"
                      hide-details
                    ></v-text-field>
                  </div>
                  <div class="ms-col-actions d-flex align-center">
                    <v-btn icon x-small color="grey" @click="removePaymentPlanItem(index)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </draggable>

              <!-- New milestone input row -->
              <div class="milestone-new-divider">
                <span class="caption text--secondary">New milestone</span>
              </div>
              <v-form ref="newMilestoneForm" lazy-validation>
                <div class="milestone-row milestone-new-row">
                  <div class="ms-col-order caption text--secondary">{{ paymentPlanItems.length + 1 }}.</div>
                  <div class="ms-col-handle"></div>
                  <div class="ms-col-pct">
                    <v-text-field
                      dense
                      outlined
                      type="number"
                      v-model.number="newPaymentPlanItem.percentage"
                      placeholder="%"
                      suffix="%"
                      min="0"
                      max="100"
                      :rules="[
                        v => (v !== null && v !== '') || 'Required',
                        v => (!isNaN(Number(v))) || 'Must be a number',
                        v => (Number(v) >= 0 && Number(v) <= 100) || '0–100',
                      ]"
                      @keyup.enter="addPaymentPlanItem"
                    ></v-text-field>
                  </div>
                  <div class="ms-col-desc">
                    <v-text-field
                      dense
                      outlined
                      v-model="newPaymentPlanItem.description"
                      placeholder="Description"
                      :rules="[
                        v => !!(v && v.trim()) || 'Required',
                      ]"
                      @keyup.enter="addPaymentPlanItem"
                    ></v-text-field>
                  </div>
                  <div class="ms-col-actions d-flex align-center">
                    <v-btn icon small color="blue darken-1" @click="addPaymentPlanItem">
                      <v-icon small>mdi-plus-circle</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-form>
            </div>

          </v-card-text>
          <v-spacer />
          <v-card-actions>
            <v-btn color="red darken-1" @click="deleteBuilding">Delete</v-btn>
            <v-spacer></v-spacer>
            <v-fade-transition>
              <span v-if="changeDetected" class="caption amber--text d-flex align-center mr-2">
                <v-icon x-small color="amber" class="mr-1">mdi-circle-medium</v-icon>Unsaved changes
              </span>
            </v-fade-transition>
            <v-btn v-if="changeDetected" text @click="discard">Discard</v-btn>
            <v-btn
              color="blue darken-1"
              :disabled="saveButtonDisabled"
              @click="save"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item value="media">
        <media
          v-if="showMedia"
          :media="this.$store.state.MediaStore.media"
        ></media>
        <v-card-subtitle v-if="!showMedia"
          >There is no default poi for this record, media is not available.
        </v-card-subtitle>
      </v-tab-item>
      <v-tab-item value="pois">
        <v-list-item>
          <v-list-item-content>
            <poi-list
              :poi-list="this.$store.state.PoiStore.pois"
              parentType="building"
              :parent="this.$store.state.BuildingStore.selectedBuilding"
            ></poi-list>
          </v-list-item-content>
        </v-list-item>
      </v-tab-item>
      <v-tab-item value="apartmentTypes">
        <v-card flat>
          <v-card-text class="pa-0">
            <v-list-item>
              <v-list-item-content>
                <apartment-type-list
                  :apartmentTypeList="
                    this.$store.state.ApartmentTypeStore.apartmentTypes
                  "
                ></apartment-type-list>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item value="apartmentOrientations">
        <v-card flat>
          <v-card-text class="pa-0">
            <v-list-item>
              <v-list-item-content>
                <apartment-orientation-list
                  :apartmentOrientationList="
                    this.$store.state.ApartmentOrientationStore
                      .apartmentOrientations
                  "
                ></apartment-orientation-list>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item value="apartments">
        <v-card flat>
          <v-card-text>
            <!-- Apartment Filters -->
            <v-row class="mb-4">
              <v-col cols="12">
                <apartment-filters
                  :filters="filters"
                  :is-loading="isLoading"
                  :floors-options="floorsOptions"
                  :rooms-options="roomsOptions"
                  @update:filters="filters = $event"
                  @apply-filters="applyFilters"
                  @reset-filters="resetFilters"
                ></apartment-filters>
              </v-col>
            </v-row>

            <!-- Apartment List -->
            <v-list-item>
              <v-list-item-content>
                <apartment-list
                  :apartmentList="this.$store.state.ApartmentStore.apartments"
                ></apartment-list>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <apply-payment-plan-dialog
      :visible="applyPlanDialogVisible"
      :payment-plan="paymentPlanItems"
      :current-building-id="parseInt($route.params.buildingId)"
      @close="applyPlanDialogVisible = false"
    ></apply-payment-plan-dialog>
  </v-card>
</template>

<script>
import ApartmentList from '../../components/RecordLists/ApartmentList'
import ApartmentTypeList from '../../components/RecordLists/ApartmentTypeList'
import ApartmentOrientationList from '../../components/RecordLists/ApartmentOrientationList'
import ProjectFlowNavigationToolbar from '../../components/AppBar/ProjectFlowNavigationToolbar'
import ApartmentFilters from '../../components/ApartmentFilters'
import ApplyPaymentPlanDialog from '../../components/ApplyPaymentPlanDialog'
import Enums from '../../helpers/Enums'
import Media from '../../views/Media'
import PoiList from '../../components/RecordLists/PoiList'
import VJsoneditor from 'v-jsoneditor/src/index'
import draggable from 'vuedraggable'
import { mapState } from 'vuex'
import MasterplanService from '../../services/MasterplanService'

export default {
  name: 'BuildingExplore',
  props: {},
  components: {
    ApartmentList,
    ApartmentTypeList,
    // eslint-disable-next-line vue/no-unused-components
    ApartmentOrientationList,
    ProjectFlowNavigationToolbar,
    ApartmentFilters,
    ApplyPaymentPlanDialog,
    Media,
    PoiList,
    VJsoneditor,
    draggable
  },
  data: function() {
    return {
      pois: [],
      building: {},
      title: '',
      tab: null,
      changeDetected: false,
      newFieldName: '',
      newFieldDefaultValue: null,
      dialog: false,
      propertiesEditorDialog: false,
      propertiesSnapshot: null,
      windowHeight: window.innerHeight,
      newPaymentPlanItem: { percentage: null, description: '' },
      isDraggingMilestone: false,
      applyPlanDialogVisible: false,
      showMedia: false,
      checkboxDisabled: false,
      // Filter properties
      filters: {
        statusIds: [],
        floorMin: null,
        floorMax: null,
        rooms: []
      },
      isLoading: false,
      // Static dropdown options (set once on initial load)
      staticFloorsOptions: [],
      staticRoomsOptions: [],
      // Compare amenities inherited by this building's apartments. Keep this
      // list in sync with helpers/compareAmenities.js in th-frontend.
      amenitiesExpanded: false,
      applyBuildingAmenitiesKey: 'applyAmenitiesToAllApartments',
      // `activities` is handled separately (multi-value combobox) below.
      activitiesKey: 'activities',
      compareAmenityKeys: [
        'parking',
        'garage',
        'beachAccess',
        'communalPool',
        'pool',
        'gym',
        'balcony',
        'playground',
        'noCarZone',
        'security247',
        'concierge'
      ],
      amenityOptions: [
        { text: 'Not set', value: null },
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' }
      ]
    }
  },
  computed: {
    buildingName: function() {
      return this.$store.state.BuildingStore.selectedBuilding
        ? this.$store.state.BuildingStore.selectedBuilding.name
        : ''
    },
    newMilestoneIsValid() {
      const pct = this.newPaymentPlanItem.percentage
      const desc = (this.newPaymentPlanItem.description || '').trim()
      const pctValid =
        pct !== null && pct !== '' && !isNaN(Number(pct)) && Number(pct) >= 0 && Number(pct) <= 100
      return pctValid && !!desc
    },
    saveButtonDisabled: function() {
      return !this.changeDetected && !this.newMilestoneIsValid
    },
    floorsOptions: function() {
      return this.staticFloorsOptions
    },
    roomsOptions: function() {
      return this.staticRoomsOptions
    },
    ...mapState('MediaStore', ['selectedGallery']),
    propertiesDialogCardStyle() {
      return {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: (this.windowHeight - 48) + 'px'
      }
    },
    propertiesDialogDirty() {
      return JSON.stringify(this.building.properties) !== JSON.stringify(this.propertiesSnapshot)
    },
    propertiesEditorHeight() {
      // 48px dialog margin + 52px card-title + 52px card-actions + 16px card-text top padding
      return Math.max(150, this.windowHeight - 168) + 'px'
    },
    paymentPlanItems() {
      return (this.building.properties && this.building.properties.paymentPlan) || []
    },
    applyBuildingAmenities: {
      get() {
        return !!(
          this.building.properties &&
          this.building.properties[this.applyBuildingAmenitiesKey]
        )
      },
      set(val) {
        if (!this.building.properties) {
          this.$set(this.building, 'properties', {})
        }
        const key = this.applyBuildingAmenitiesKey
        if (val) {
          this.$set(this.building.properties, key, true)
        } else {
          this.$delete(this.building.properties, key)
        }
      }
    },
    activitiesModel: {
      get() {
        const val = this.building.properties && this.building.properties.activities
        return Array.isArray(val) ? val : []
      },
      set(val) {
        if (!this.building.properties) {
          this.$set(this.building, 'properties', {})
        }
        if (val && val.length) {
          this.$set(this.building.properties, this.activitiesKey, val)
        } else {
          this.$delete(this.building.properties, this.activitiesKey)
        }
      }
    }
  },
  watch: {
    selectedGallery: async function(newValue) {
      await this.$store.dispatch('MediaStore/fetchMedia')
    },
    // Watch for apartments changes to initialize static options
    '$store.state.ApartmentStore.apartments': function(newApartments) {
      if (
        newApartments &&
        newApartments.length > 0 &&
        (this.staticFloorsOptions.length === 0 || this.staticRoomsOptions.length === 0)
      ) {
        this.initializeStaticOptions()
      }
    },
    building: {
      // This will let Vue know to look inside the object
      deep: true,
      handler: function(after, before) {
        if (JSON.stringify(before) !== '{}') {
          this.changeDetected =
            JSON.stringify(this.building) !==
            JSON.stringify(this.$store.state.BuildingStore.selectedBuilding)
        }
      }
    }
  },
  methods: {
    getData: async function(buildingId, masterplanId) {
      if (buildingId) {
        await this.$store.dispatch('MediaStore/resetStore')

        await this.$store.dispatch(
          'BuildingStore/setSelectedBuilding',
          buildingId
        )
        this.building = JSON.parse(
          JSON.stringify(this.$store.state.BuildingStore.selectedBuilding)
        )
        const masterplan = await MasterplanService.getMasterplanById(
          masterplanId
        )
        if (!masterplan.data.publish) {
          this.checkboxDisabled = true
        }
        await this.$store.dispatch('ApartmentStore/fetchApartments', {
          root: true
        })
        await this.$store.dispatch('ApartmentTypeStore/fetchApartmentTypes', {
          root: true
        })
        // Initialize static dropdown options after all data is loaded
        this.initializeStaticOptions()
        await this.$store.dispatch(
          'ApartmentOrientationStore/fetchApartmentOrientations',
          {
            root: true
          }
        )

        await this.$store.dispatch('PoiStore/setPoiContext', 'building')
        await this.$store.dispatch('PoiStore/fetchPois')

        // find default poi
        let defaultPoi = this.$store.state.PoiStore.pois.find(el => {
          return el.poiTypeId === Enums.PoiType.poi_type_building
        })
        if (defaultPoi) {
          await this.$store.dispatch('PoiStore/setSelectedPoi', defaultPoi.id)
          await this.$store.dispatch('MediaStore/setMediaContext', 'poi')
          await this.$store.dispatch('MediaStore/fetchMediaTypes')
          await this.$store.dispatch('MediaStore/fetchMedia')
          this.showMedia = true
        } else {
          this.showMedia = false
        }
      }
    },
    save: async function() {
      const pct = this.newPaymentPlanItem.percentage
      const desc = (this.newPaymentPlanItem.description || '').trim()
      const pctValid =
        pct !== null && pct !== '' && !isNaN(Number(pct)) && Number(pct) >= 0 && Number(pct) <= 100
      if (pctValid && desc) {
        this.addPaymentPlanItem()
      }
      await this.$store.dispatch('BuildingStore/updateBuilding', this.building)
      // Upisivanje updateovanog buildinga se sada izvrsava u updateBuilding akciji:
      // await this.$store.dispatch(
      //   'BuildingStore/setSelectedBuilding',
      //   this.$route.params.buildingId
      // )
      this.building = JSON.parse(
        JSON.stringify(this.$store.state.BuildingStore.selectedBuilding)
      )
      this.changeDetected = false
    },
    saveNewField: function() {
      let self = this
      if (!this.building.properties) this.building.properties = {}
      if (this.building.properties.hasOwnProperty(this.newFieldName)) {
        this.$store.dispatch(
          'NotificationStore/add',
          {
            type: 'error',
            message: `The field with the given name already exists.`
          },
          { root: true }
        )
      } else {
        this.building.properties[self.newFieldName] = self.newFieldDefaultValue
      }
      this.closeNewFieldDialog()
    },
    closeNewFieldDialog: function() {
      this.newFieldName = ''
      this.newFieldDefaultValue = null
      this.dialog = false
    },
    // Keys rendered via dedicated controls rather than the generic JSON
    // text-field list (payment plan editor + compare amenities section).
    isHiddenPropertyKey: function(key) {
      return (
        key === 'paymentPlan' ||
        key === this.applyBuildingAmenitiesKey ||
        key === this.activitiesKey ||
        this.compareAmenityKeys.includes(key)
      )
    },
    getCompareAmenity: function(key) {
      if (!this.building.properties) return null
      const val = this.building.properties[key]
      return val === undefined ? null : val
    },
    setCompareAmenity: function(key, val) {
      if (!this.building.properties) this.$set(this.building, 'properties', {})
      if (val === null || val === undefined) {
        this.$delete(this.building.properties, key)
      } else {
        this.$set(this.building.properties, key, val)
      }
    },
    addPaymentPlanItem() {
      if (!this.$refs.newMilestoneForm.validate()) return
      const pct = this.newPaymentPlanItem.percentage
      const desc = (this.newPaymentPlanItem.description || '').trim()
      if (!this.building.properties) this.$set(this.building, 'properties', {})
      if (!this.building.properties.paymentPlan) this.$set(this.building.properties, 'paymentPlan', [])
      this.building.properties.paymentPlan.push({
        percentage: Number(pct),
        description: desc,
        order: this.building.properties.paymentPlan.length + 1
      })
      this.newPaymentPlanItem = { percentage: null, description: '' }
      this.$nextTick(() => this.$refs.newMilestoneForm.resetValidation())
    },
    openPropertiesDialog() {
      this.propertiesSnapshot = JSON.parse(JSON.stringify(this.building.properties || null))
      this.propertiesEditorDialog = true
    },
    discardPropertiesDialog() {
      this.$set(this.building, 'properties', JSON.parse(JSON.stringify(this.propertiesSnapshot)))
      this.propertiesEditorDialog = false
    },
    discard() {
      this.building = JSON.parse(JSON.stringify(this.$store.state.BuildingStore.selectedBuilding))
      this.changeDetected = false
    },
    onJsonEditorChange() {},
    onWindowResize() {
      this.windowHeight = window.innerHeight
    },
    onPaymentPlanReorder() {
      this.isDraggingMilestone = false
      this.building.properties.paymentPlan.forEach((item, i) => {
        item.order = i + 1
      })
    },
    removePaymentPlanItem(index) {
      this.building.properties.paymentPlan.splice(index, 1)
      this.building.properties.paymentPlan.forEach((item, i) => {
        item.order = i + 1
      })
    },
    deleteBuilding: async function() {
      this.$confirm({
        title: 'Delete Building',
        text: 'Are you sure?',
        acceptText: 'DELETE',
        cancelText: 'CANCEL'
      })
        .then(y => {
          this.$store
            .dispatch(
              'BuildingStore/deleteBuilding',
              this.$route.params.buildingId
            )
            .then(
              () => {
                this.$router.go(-1)
                this.$emit('deleted')
              },
              err => {
                this.$store.dispatch(
                  'NotificationStore/add',
                  {
                    type: 'error',
                    message: 'Building has child records, it cannot be deleted.'
                  },
                  { root: true }
                )
              }
            )
        })
        .catch(n => {
          console.log('NO')
        })
    },
    applyFilters: async function() {
      this.isLoading = true

      // Build query parameters object
      const queryParams = {}

      if (this.filters.statusIds && this.filters.statusIds.length > 0) {
        queryParams.statusIds = this.filters.statusIds.join(',')
      }

      // Handle floor min/max logic
      if (this.filters.floorMin !== null && this.filters.floorMin !== '') {
        queryParams.floorMin = this.filters.floorMin
      }

      if (this.filters.floorMax !== null && this.filters.floorMax !== '') {
        queryParams.floorMax = this.filters.floorMax
      }

      // If only min is set, set max to maximum available floor
      if (
        this.filters.floorMin !== null &&
        this.filters.floorMin !== '' &&
        (this.filters.floorMax === null || this.filters.floorMax === '')
      ) {
        const maxFloor = Math.max(
          ...this.floorsOptions.map(option => option.value)
        )
        queryParams.floorMax = maxFloor
      }

      // If only max is set, set min to minimum available floor
      if (
        this.filters.floorMax !== null &&
        this.filters.floorMax !== '' &&
        (this.filters.floorMin === null || this.filters.floorMin === '')
      ) {
        const minFloor = Math.min(
          ...this.floorsOptions.map(option => option.value)
        )
        queryParams.floorMin = minFloor
      }

      if (this.filters.rooms && this.filters.rooms.length > 0) {
        queryParams.rooms = this.filters.rooms.join(',')
      }

      try {
        await this.$store.dispatch(
          'ApartmentStore/fetchApartments',
          queryParams
        )
      } catch (error) {
        console.error('Error applying filters:', error)
      } finally {
        this.isLoading = false
      }
    },
    resetFilters: function() {
      this.filters = {
        statusIds: [],
        floorMin: null,
        floorMax: null,
        rooms: []
      }
      // Apply empty filters to reset the apartment list
      this.applyFilters()
    },
    initializeStaticOptions: function() {
      // Initialize floors options from all apartments
      const apartments = this.$store.state.ApartmentStore.apartments || []
      if (apartments.length > 0) {
        const uniqueFloors = [
          ...new Set(
            apartments
              .map(apt => apt.floor)
              .filter(floor => floor !== null && floor !== undefined)
          )
        ]
        this.staticFloorsOptions = uniqueFloors
          .sort((a, b) => a - b)
          .map(floor => ({
            text: floor.toString(),
            value: floor
          }))
      }

        // Initialize rooms options
        const uniqueRooms = [
          ...new Set(
            apartments
              .map(apt => apt.rooms)
              .filter(rooms => rooms !== null && rooms !== undefined)
          )
        ]
        this.staticRoomsOptions = uniqueRooms
          .sort((a, b) => a - b)
          .map(rooms => ({
            text: rooms.toString(),
            value: rooms
          }))
    }
  },
  created: async function() {
    // if (localStorage.exploreBuildingActiveTab) {
    //   this.tab = localStorage.exploreBuildingActiveTab
    // }
    let lastBreadcrumb = this.$store.state.BreadcrumbsStore.history[
      this.$store.state.BreadcrumbsStore.history.length - 2
    ]
    if (lastBreadcrumb && lastBreadcrumb.tab) {
      if (lastBreadcrumb.tab === 'APARTMENT ') {
        this.tab = 'apartments'
      }
      if (lastBreadcrumb.tab === 'APARTMENT TYPE ') {
        this.tab = 'apartmentTypes'
      }
      if (lastBreadcrumb.tab === 'P.O.I ') {
        this.tab = 'pois'
      }
    }
  },
  mounted: function() {
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy: function() {
    window.removeEventListener('resize', this.onWindowResize)
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getData(
        parseInt(to.params.buildingId),
        parseInt(to.params.masterplanId)
      )
    })
  },
  async beforeRouteUpdate(to, from, next) {
    this.getData(to.params.buildingId, to.params.masterplanId)
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (
      to.name == 'Explore Poi' ||
      to.name == 'Explore Apartment Type' ||
      to.name == 'Explore Apartment'
    ) {
      localStorage.exploreBuildingActiveTab = this.tab
    } else {
      localStorage.exploreBuildingActiveTab = 'details'
    }
    next()
  }
}
</script>

<style scoped>
.json-preview {
  background: rgba(255, 255, 255, 0.07);
  transition: background 0.15s ease, color 0.15s ease;
}
.json-preview:hover {
  background: rgba(255, 255, 255, 1);
  color: rgba(0, 0, 0, 0.87);
}
/* milestone table — flexbox so headers always align with rows */
.milestone-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}
.milestone-header {
  padding-bottom: 2px;
}
.ms-col-order {
  width: 28px;
  flex-shrink: 0;
  text-align: right;
}
.ms-col-handle {
  width: 28px;
  flex-shrink: 0;
}
.ms-col-pct {
  width: 100px;
  flex-shrink: 0;
}
.ms-col-desc {
  flex: 1;
  min-width: 0;
}
.ms-col-actions {
  width: 32px;
  flex-shrink: 0;
}
.drag-handle {
  cursor: grab;
  opacity: 0.5;
  transition: opacity 0.15s ease;
}
.milestone-row:hover .drag-handle {
  opacity: 1;
}
.drag-handle:active {
  cursor: grabbing;
}
.milestone-drag-ghost {
  opacity: 0.35;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 4px;
}
.milestone-new-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
}
.milestone-new-divider::before,
.milestone-new-divider::after {
  content: '';
  flex: 1;
  border-top: 1px dashed rgba(255, 255, 255, 0.15);
}
.milestone-new-row {
  border-radius: 4px;
  padding: 4px 6px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
}
.milestone-new-row:focus-within {
  border-color: rgba(100, 181, 246, 0.5);
  background: rgba(33, 150, 243, 0.05);
}
</style>
